import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RoadmapSection } from './RoadmapSection';
import { BookDemoModal } from '@components/ui/BookDemoModal';
import { Reveal } from '@components/ui/Reveal';
import { paymentService } from '@services/api/payment.service';
import { featureService } from '@services/api/feature.service';
import type { SubscriptionPlan } from '@/types/payment';
import { LANDING_FAQ as FAQ } from '@config/landingContent';

// ── Marketing copy ────────────────────────────────────────────────────────────
// Everything claimed on this page is a shipped feature (see config/featureRegistry.ts for where each lives).

const HERO_FEATURES = [
  { icon: '🎙️', label: 'AI Voice Teacher' },
  { icon: '📹', label: 'Live Classes' },
  { icon: '📖', label: 'Reading & Handwriting Coach' },
  { icon: '📊', label: 'Parent Reports' },
];

const svg = (d: string) => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const FEATURES = [
  {
    icon: svg('M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z'),
    title: 'AI Voice Teacher',
    desc: 'Explains every topic aloud, part by part. Pause anytime and resume from the same word, or raise your hand to ask — it answers, then carries on.',
    highlight: true,
    badge: 'New',
  },
  {
    icon: svg('M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342'),
    title: 'AI Classroom Board',
    desc: 'Formulas, diagrams, worked solutions and timelines appear on a live board that follows the explanation — styled for Class 1 to Class 10.',
    highlight: true,
    badge: 'AI-Powered',
  },
  {
    icon: svg('M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z'),
    title: 'Live Interactive Classes',
    desc: 'Teacher-led live sessions with video, a shared whiteboard, hand-raise, camera blur, and automatic recording.',
    highlight: true,
    badge: 'Most Popular',
  },
  {
    icon: svg('M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25'),
    title: 'Reading Coach',
    desc: 'Your child reads aloud; every word is checked for pronunciation and fluency, with replay of their own reading and clear advice.',
    highlight: false,
  },
  {
    icon: svg('M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10'),
    title: 'Handwriting Coach',
    desc: 'Snap a photo of the notebook page and get feedback on legibility and accuracy — plus fresh AI dictation paragraphs to practise.',
    highlight: false,
  },
  {
    icon: svg('M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z'),
    title: 'Quizzes, Assignments & Tests',
    desc: 'Topic quizzes with instant scoring, assignments with AI-assisted evaluation, and full course tests to check exam readiness.',
    highlight: false,
  },
  {
    icon: svg('M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z'),
    title: 'AI Progress Reports',
    desc: 'An Olympiad-style report of strengths, mistakes and a personal study plan — written for parents, not just teachers.',
    highlight: false,
  },
  {
    icon: svg('M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'),
    title: 'Lesson Plans & Study Material',
    desc: 'A complete self-study guide per topic — concepts, worked examples, practice — downloadable as PDF, next to notes, videos and your teacher’s recordings.',
    highlight: false,
  },
  {
    icon: svg('M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z'),
    title: 'Ask Anything, Anytime',
    desc: 'Type any question and get a clear, step-by-step answer with maths and visuals — or raise a doubt ticket for human follow-up.',
    highlight: false,
  },
];

const STEPS = [
  { n: '01', title: 'Pick your class', desc: 'Tell us your class and board — matching courses are added to your account automatically' },
  { n: '02', title: 'Learn with your AI teacher', desc: 'Press Start: the teacher explains, the board follows. Pause, resume or raise your hand anytime' },
  { n: '03', title: 'Practise with real feedback', desc: 'Quizzes, reading aloud, handwriting photos and course tests — each checked by AI' },
  { n: '04', title: 'See the progress', desc: 'Progress reports and reading reports show exactly what improved and what to do next' },
];

// PLACEHOLDER testimonials and stats (kept from the previous page) — replace with real quotes/numbers before launch.
// Until then they are not shown: invented reviews and figures are a trust risk, and search engines penalise them.
// Put real content in TESTIMONIALS / STATS and set this to true.
const SHOW_SOCIAL_PROOF = false;
const TESTIMONIALS = [
  { name: 'Priya Sharma', role: 'Grade 10 Student', quote: 'The live class whiteboard is amazing — I can see exactly what the teacher draws and ask questions without interrupting.', stars: 5 },
  { name: 'Rajan Mehta', role: 'Teacher, Mathematics', quote: 'I deliver classes to 40+ students simultaneously. The recording feature means no student ever misses a lesson.', stars: 5 },
  { name: 'Sunita Patel', role: 'Parent', quote: 'My son replays classes he found difficult. The AI tutor explains concepts in simple language suited to his grade.', stars: 5 },
];

const STATS = [
  { value: '10K+', label: 'Active Learners' },
  { value: '200+', label: 'Courses Available' },
  { value: '50K+', label: 'Doubts Resolved' },
  { value: '99%', label: 'Session Uptime' },
];


// ── App preview mockups (CSS-based) ───────────────────────────────────────────

const LiveClassMockup: React.FC = () => (
  <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-slate-200" style={{ fontFamily: 'system-ui, sans-serif' }}>
    <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
      <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-yellow-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
      <div className="flex-1 text-center"><span className="text-xs text-slate-400 font-mono">Live Class — Mathematics · Grade 9</span></div>
      <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /><span className="text-[10px] text-red-400 font-bold">LIVE</span></div>
    </div>
    <div className="bg-slate-900 flex" style={{ height: '220px' }}>
      <div className="flex-1 bg-white m-2 mr-1 rounded-lg overflow-hidden relative">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #cbd5e1 0, #cbd5e1 1px, transparent 0, transparent 25px), repeating-linear-gradient(90deg, #cbd5e1 0, #cbd5e1 1px, transparent 0, transparent 25px)' }} />
        <div className="absolute top-3 left-4 text-slate-700 text-sm font-semibold">Quadratic Equations</div>
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160" xmlns="http://www.w3.org/2000/svg">
          <text x="20" y="55" fontSize="14" fill="#1e293b" fontFamily="serif">ax² + bx + c = 0</text>
          <line x1="20" y1="65" x2="180" y2="65" stroke="#94a3b8" strokeWidth="0.5" />
          <text x="20" y="90" fontSize="12" fill="#4f46e5" fontFamily="serif">x = −b ± √(b²− 4ac)</text>
          <line x1="96" y1="95" x2="210" y2="95" stroke="#4f46e5" strokeWidth="1" />
          <text x="152" y="110" fontSize="11" fill="#4f46e5" fontFamily="serif">2a</text>
          <path d="M 20 130 Q 80 90 140 130 Q 200 170 260 130" stroke="#ef4444" strokeWidth="1.5" fill="none" />
        </svg>
        <div className="absolute bottom-2 right-2 flex gap-1">
          {['✏️', '🖊', '⬜', '🗑'].map(t => <div key={t} className="w-6 h-6 bg-slate-100 rounded text-center text-xs leading-6">{t}</div>)}
        </div>
      </div>
      <div className="w-28 flex flex-col gap-1 m-2 ml-1">
        <div className="bg-slate-700 rounded-lg p-1.5 flex-1 flex flex-col gap-1 overflow-hidden">
          <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Students (12)</div>
          {['Arjun', 'Priya', 'Rohan', 'Maya', 'Karan'].map(n => (
            <div key={n} className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-indigo-600 flex items-center justify-center text-[8px] text-white font-bold flex-shrink-0">{n[0]}</div>
              <span className="text-[9px] text-slate-300 truncate">{n}</span>
              <span className="ml-auto text-[8px]">🔇</span>
            </div>
          ))}
          <div className="text-[9px] text-slate-500 mt-1">+7 more</div>
        </div>
        <div className="bg-amber-500/20 border border-amber-500/30 rounded p-1 text-center">
          <span className="text-[9px] text-amber-400 font-medium">✋ Priya raised hand</span>
        </div>
      </div>
    </div>
    <div className="bg-slate-800 px-4 py-2 flex items-center justify-center gap-3">
      {[
        { icon: '🎤', label: 'Muted', active: false },
        { icon: '📹', label: 'Camera', active: true },
        { icon: '🖥', label: 'Share', active: false },
        { icon: '⏺', label: 'Record', active: true },
      ].map(b => (
        <div key={b.label} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium ${b.active ? 'bg-indigo-600/30 text-indigo-400' : 'text-slate-400'}`}>
          <span>{b.icon}</span><span>{b.label}</span>
        </div>
      ))}
      <div className="ml-auto px-3 py-1 bg-red-600 text-white text-[10px] font-bold rounded-lg">End Class</div>
    </div>
  </div>
);

/** The MyInstructor experience: chalkboard that follows the voice, lesson progress, Pause / Raise-hand tray. */
const AITeacherMockup: React.FC = () => (
  <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-slate-200" style={{ fontFamily: 'system-ui, sans-serif' }}>
    <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
      <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-xs">🎓</div>
      <div><div className="text-xs text-white font-semibold">AI Teacher — Refraction of Light</div><div className="text-[10px] text-slate-400">Physics · Class 10 · Part 2 of 5</div></div>
      <div className="ml-auto flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /><span className="text-[10px] text-emerald-400">Explaining…</span></div>
    </div>
    <div className="h-1 bg-slate-700"><div className="h-full w-2/5 bg-emerald-500" /></div>
    <div className="p-4 space-y-2.5" style={{ background: '#14281d', minHeight: 180 }}>
      <div className="text-amber-300 text-sm font-semibold" style={{ fontFamily: 'Georgia, serif' }}>Snell’s Law</div>
      <div className="rounded-md px-2 py-1 inline-block text-[13px] text-white" style={{ background: 'rgba(232,160,32,0.25)', fontFamily: 'Georgia, serif' }}>
        n₁ sin θ₁ = n₂ sin θ₂
      </div>
      <div className="text-[11px] text-emerald-50/80 leading-relaxed">
        <span className="bg-amber-400/25 rounded px-0.5">Light slows down in a denser medium</span>, so it bends <em>towards</em> the normal —
        like a wheel rolling from grass onto mud.
      </div>
      <div className="flex gap-2 pt-1">
        {['Step 1: n₁ = 1.00', 'Step 2: sin θ₂ = 0.53', '∴ θ₂ ≈ 32°'].map(s => (
          <span key={s} className="text-[10px] text-emerald-100 bg-white/10 rounded px-1.5 py-0.5">{s}</span>
        ))}
      </div>
    </div>
    <div className="bg-slate-900 px-3 py-2.5 flex items-center justify-center gap-2">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center text-white text-sm shadow-lg">⏸</div>
      {[{ i: '✋', l: 'Ask' }, { i: '🧩', l: 'Example' }, { i: '📝', l: 'Quiz' }, { i: '📚', l: 'Lesson Plan' }, { i: '✍️', l: 'Handwriting' }].map(b => (
        <div key={b.l} className="flex flex-col items-center gap-0.5 px-1.5">
          <span className="w-8 h-7 rounded-md bg-white/10 flex items-center justify-center text-xs">{b.i}</span>
          <span className="text-[8px] text-slate-400">{b.l}</span>
        </div>
      ))}
    </div>
  </div>
);

const ReadingMockup: React.FC = () => (
  <div className="rounded-2xl bg-white border border-slate-200 shadow-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Reading report</span>
      <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">86% accuracy</span>
    </div>
    <p className="text-sm leading-7 text-slate-700">
      The <span className="bg-emerald-100 rounded px-0.5">little</span> bird <span className="bg-rose-100 text-rose-700 rounded px-0.5 underline decoration-wavy">flew</span> over
      the <span className="bg-amber-100 rounded px-0.5">meadow</span> and <span className="line-through text-slate-400">softly</span> landed on a branch.
    </p>
    <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
      <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">● mispronounced</span>
      <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">● different word</span>
      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">● skipped</span>
    </div>
    <div className="mt-3 text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5">
      🔊 “flew” — say it as <b>floo</b>, one smooth sound. Tap the word to hear it, then replay your own reading.
    </div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

export const Landing: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeMockup, setActiveMockup] = useState<'ai' | 'live'>('ai');
  // "/?demo=1" opens the booking form straight away: the link printed on Video Studio end cards
  // and added to video descriptions. (Guarded for the prerender step, which has no window.)
  const [showDemoModal, setShowDemoModal] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === '1',
  );
  const [demoIsSchool, setDemoIsSchool] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  // Features an admin marked "Coming soon" in Feature Management -- shown with a badge, not as available.
  const [soonKeys, setSoonKeys] = useState<Set<string>>(new Set());
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Pricing teaser reads live prices; if the call fails the section still renders, just without numbers.
  useEffect(() => {
    paymentService.listPlans().then((p) => setPlans(p || [])).catch(() => setPlans([]));
    featureService.listFeatures()
      .then((fs) => setSoonKeys(new Set((fs || []).filter((f) => f.isComingSoon).map((f) => f.key))))
      .catch(() => setSoonKeys(new Set()));
  }, []);

  const monthly = (group: string) => plans.find((p) => (p.planGroup || p.planCode) === group && p.billingCycle === 'monthly');
  const school = plans.find((p) => p.planCode === 'school');
  const inr = (n?: number) => (n !== undefined ? `₹${n.toLocaleString('en-IN')}` : '');

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>

      {/* ── NAV ─────────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${scrolled ? 'bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 flex items-center h-16 gap-3 sm:gap-6">
          <div className="flex items-center gap-2 sm:gap-2.5 mr-auto min-w-0">
            <div className="w-8 h-8 flex-shrink-0 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-base">🎓</div>
            <span className="text-base sm:text-lg font-bold text-slate-900 tracking-tight whitespace-nowrap">Aahana <span className="text-indigo-600">SmartTutor</span></span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-slate-900 transition-colors">Features</a>
            <a href="#ai-teacher" className="hover:text-slate-900 transition-colors">AI Teacher</a>
            <a href="#schools" className="hover:text-slate-900 transition-colors">For Schools</a>
            <Link to="/pricing" className="hover:text-slate-900 transition-colors">Pricing</Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/auth/login" className="inline-flex items-center justify-center px-2.5 sm:px-4 py-2 text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors rounded-lg hover:bg-slate-50 whitespace-nowrap">
              Sign in
            </Link>
            <Link to="/auth/register" className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm whitespace-nowrap">
              <span className="sm:hidden">Try free</span><span className="hidden sm:inline">Try free for 14 days</span>
            </Link>
          </div>
        </div>
      </nav>

      <main>
      {/* ── HERO ────────────────────────────────────────────── */}
      <section className="pt-28 pb-16 px-5 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          {/* Hero: a quick fade on load only -- the first screen must never wait on a scroll animation. */}
          <div className="grid lg:grid-cols-2 gap-12 items-center animate-fade-in motion-reduce:animate-none">
            <div>
              <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                AI teacher + live classes for Class 1–10
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-slate-900 leading-tight tracking-tight mb-5">
                Your child’s personal AI teacher.<br />
                <span className="text-indigo-600">Patient, clear, always there.</span>
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-lg">
                It explains every topic aloud on a live board, stops when your child raises a hand, checks their reading and handwriting,
                and shows you exactly how they’re improving.
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                <Link to="/auth/register" className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors shadow-md text-sm">
                  Start your 14-day free trial →
                </Link>
                <button onClick={() => { setDemoIsSchool(false); setShowDemoModal(true); }} className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-semibold rounded-xl transition-colors border border-slate-300 text-sm">
                  Book a Live Demo
                </button>
              </div>
              <p className="text-xs text-slate-400 mb-8">Every feature included in the trial · no card needed</p>
              <div className="flex flex-wrap gap-2">
                {HERO_FEATURES.map(f => (
                  <div key={f.label} className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                    <span>{f.icon}</span><span>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="flex gap-2 mb-4">
                {(['ai', 'live'] as const).map(m => (
                  <button key={m} onClick={() => setActiveMockup(m)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${activeMockup === m ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                    {m === 'ai' ? '🎓 AI Teacher' : '📹 Live Class'}
                  </button>
                ))}
              </div>
              {/* keyed so switching tabs cross-fades instead of snapping */}
              <div key={activeMockup} className="animate-fade-in motion-reduce:animate-none">
                {activeMockup === 'ai' ? <AITeacherMockup /> : <LiveClassMockup />}
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-indigo-100 rounded-full blur-2xl opacity-60 pointer-events-none" />
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-blue-100 rounded-full blur-xl opacity-40 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS (placeholder numbers — see note above STATS) ── */}
      {SHOW_SOCIAL_PROOF && <section className="py-12 border-y border-slate-100 bg-white">
        <div className="max-w-5xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <p className="text-3xl font-extrabold text-indigo-600 mb-1">{s.value}</p>
              <p className="text-sm text-slate-500 font-medium">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>}

      {/* ── FEATURES ────────────────────────────────────────── */}
      <section id="features" className="py-20 px-5 lg:px-8 bg-slate-50 scroll-mt-16">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">Everything a learner needs, in one place</h2>
            <p className="text-slate-500 text-lg max-w-xl mx-auto">From the first explanation to the final test — taught, practised, checked and reported</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <Reveal key={i} delay={(i % 3) * 90}>
              <div className={`relative h-full p-6 rounded-2xl border transition-shadow hover:shadow-md ${f.highlight ? 'bg-white border-indigo-200 shadow-sm' : 'bg-white border-slate-200'}`}>
                {f.badge && (
                  <div className={`absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full ${f.badge === 'Most Popular' ? 'bg-indigo-100 text-indigo-700' : f.badge === 'AI-Powered' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {f.badge}
                  </div>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${f.highlight ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-600'}`}>
                  {f.icon}
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── MEET YOUR AI TEACHER ────────────────────────────── */}
      <section id="ai-teacher" className="py-20 px-5 lg:px-8 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="order-2 lg:order-1"><AITeacherMockup /></Reveal>
          <Reveal delay={120} className="order-1 lg:order-2">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-indigo-600 mb-3">Meet your AI teacher</p>
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-5">
              It teaches like the best tutor you know — <span className="text-indigo-600">and never loses patience</span>
            </h2>
            <ul className="space-y-4">
              {[
                ['🗣️', 'Explains aloud, part by part', 'Each topic is split into short parts. The board highlights exactly what’s being said, so eyes and ears stay together.'],
                ['⏸️', 'Pause anytime, resume from the same word', 'Need a moment to think, or a quick break? Press pause (or Space). Resume picks up mid-sentence — nothing is skipped.'],
                ['✋', 'Raise your hand to ask', 'The mic opens only when your child raises a hand. The teacher answers the doubt, then carries on from where it stopped.'],
                ['🎭', 'Face-to-face avatar', 'Turn on a lip-synced teacher avatar for a one-on-one feel — or keep it voice-only.', 'avatar_live_tutor'],
                ['🧩', 'Examples, use cases, lesson plans', 'One tap for a worked example, a real-life use case, or a complete lesson plan to download as PDF.'],
              ].map(([icon, title, desc, featureKey]) => (
                <li key={title} className="flex gap-3">
                  <span className="text-xl flex-shrink-0 w-8 text-center" aria-hidden>{icon}</span>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">
                      {title}
                      {featureKey && soonKeys.has(featureKey) && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 align-middle">
                          Coming soon
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── PRACTICE WITH REAL FEEDBACK ─────────────────────── */}
      <section className="py-20 px-5 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-600 mb-3">Practice that gives real feedback</p>
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-5">Not just “correct” or “wrong” — <span className="text-emerald-600">what to fix, and how</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ['📖', 'Reading coach', 'Per-word pronunciation and fluency feedback. Tap any word to hear it; replay your own reading.'],
                ['✍️', 'Handwriting coach', 'Photograph the notebook page for legibility and accuracy feedback, with fresh AI dictation to practise.'],
                ['📝', 'Quizzes & assignments', 'Instant scoring with explanations; assignments evaluated with AI assistance.'],
                ['🎯', 'Course tests', 'Full tests to check readiness, with retakes and an AI report on every attempt.'],
              ].map(([icon, title, desc]) => (
                <div key={title} className="bg-white rounded-2xl border border-slate-200 p-4">
                  <div className="text-2xl mb-2" aria-hidden>{icon}</div>
                  <p className="font-bold text-sm text-slate-900 mb-1">{title}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}><ReadingMockup /></Reveal>
        </div>
      </section>

      {/* ── PARENTS SEE REAL PROGRESS ───────────────────────── */}
      <section className="py-20 px-5 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-amber-600 mb-3">For parents</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">See real progress, not just screen time</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Clear reports tell you what your child has mastered, where they slip, and what to practise next.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              ['📊', 'AI progress report', 'An Olympiad-style report after each course test: strengths, common mistakes, and a personal study plan.'],
              ['📈', 'Reading reports', 'Accuracy, fluency and the words to practise, tracked over time across every reading session.'],
              ['🗓️', 'Streaks & study calendar', 'Daily learning streaks, completed topics and upcoming classes, all on the dashboard.'],
            ].map(([icon, title, desc], i) => (
              <Reveal key={title} delay={i * 100}>
              <div className="h-full rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center text-xl mb-4" aria-hidden>{icon}</div>
                <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────── */}
      <section id="how-it-works" className="py-20 px-5 lg:px-8 bg-slate-50 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">Up and running in minutes</h2>
            <p className="text-slate-500 text-base">Four simple steps from sign-up to seeing results</p>
          </Reveal>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-slate-200" />
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 110} className="flex flex-col items-center text-center relative">
                <div className="w-20 h-20 rounded-2xl bg-indigo-600 text-white flex flex-col items-center justify-center mb-4 shadow-lg relative z-10">
                  <span className="text-[10px] font-bold opacity-70 uppercase tracking-widest">{s.n}</span>
                  <span className="text-xs font-bold mt-0.5 leading-tight text-center px-2">{s.title.split(' ').slice(0, 2).join(' ')}</span>
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <RoadmapSection />

      {/* ── HIGHLIGHT: Live Class deep-dive ─────────────────── */}
      <section className="py-20 px-5 lg:px-8 bg-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse" /> LIVE CLASSROOM
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-5">
              Real teachers, live — <br className="hidden lg:block" />
              <span className="text-indigo-400">with every class recorded</span>
            </h2>
            <p className="text-slate-300 text-base leading-relaxed mb-8">
              When a human teacher makes the difference, join live: video, a shared whiteboard, hand-raise approval,
              and automatic recording so no student ever misses a lesson.
            </p>
            <ul className="space-y-3">
              {[
                'Pressure-sensitive pen-tablet whiteboard',
                'Screen share + camera side-by-side, with camera blur',
                'Hand-raise → teacher approve → student unmutes',
                'Auto-recorded and available to replay',
                'Upload PDFs and images during class',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120} className="flex flex-col gap-4">
            <LiveClassMockup />
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: '⏺', label: 'Auto Recording', sub: 'Never miss a session' },
                { icon: '📁', label: 'Study Materials', sub: 'Share files live' },
                { icon: '🎬', label: 'Replay', sub: 'Watch at any speed' },
              ].map(c => (
                <div key={c.label} className="bg-slate-800 rounded-xl p-3 text-center border border-slate-700">
                  <div className="text-xl mb-1">{c.icon}</div>
                  <div className="text-xs font-bold text-white">{c.label}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{c.sub}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOR SCHOOLS ─────────────────────────────────────── */}
      <section id="schools" className="py-20 px-5 lg:px-8 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-indigo-600 mb-3">For schools & coaching institutes</p>
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-5">
              A personal AI teacher for every student. <span className="text-indigo-600">A console for you.</span>
            </h2>
            <p className="text-slate-500 leading-relaxed mb-6">
              Give every student the full AI learning experience, give teachers the tools to build and record their own lessons,
              and see every class’s progress in one place.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => { setDemoIsSchool(true); setShowDemoModal(true); }} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-md text-sm">
                Book a school demo
              </button>
              <Link to="/pricing#schools" className="inline-flex items-center justify-center px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-semibold rounded-xl border border-slate-300 text-sm">
                School pricing{school ? ` · ${inr(school.price)}/student/${school.billingCycle === 'yearly' ? 'yr' : 'mo'}` : ''}
              </Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ['🏫', 'School console', 'Seats, teacher & student invites, and class-wise progress at a glance.'],
              ['📥', 'Onboard in minutes', 'Import students by CSV with their class and section; each gets an email invite.'],
              ['🧑‍🏫', 'AI course builder', 'Teachers design courses in a chat with AI and generate lesson content in one click.'],
              ['🎥', 'Record with teacher', 'Record video, voice or board lessons and publish any number of takes to Study Material.'],
              ['📹', 'Live classes', 'Teach live with whiteboard, hand-raise and automatic recording.'],
              ['✅', 'Test builder', 'Create course tests and question banks for your students.'],
            ].map(([icon, title, desc], i) => (
              <Reveal key={title} delay={(i % 2) * 90}>
              <div className="h-full rounded-2xl border border-slate-200 p-4 bg-slate-50">
                <div className="text-xl mb-1.5" aria-hidden>{icon}</div>
                <p className="font-bold text-sm text-slate-900 mb-1">{title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
              </div>
              </Reveal>
            ))}
          </div>
        </div>

      </section>

      {/* ── TESTIMONIALS (placeholder — see note above TESTIMONIALS) ── */}
      {SHOW_SOCIAL_PROOF && <section className="py-20 px-5 lg:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">Trusted by students and teachers</h2>
            <p className="text-slate-500">Real experiences from our community</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 100}>
              <div className="h-full p-6 bg-white rounded-2xl border border-slate-200">
                <div className="flex text-amber-400 text-sm mb-4">{'★'.repeat(t.stars)}</div>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{t.name[0]}</div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>}

      {/* ── PRICING TEASER ──────────────────────────────────── */}
      <section id="pricing" className="py-20 px-5 lg:px-8 bg-white scroll-mt-16">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">Plans for every learner</h2>
            <p className="text-slate-500">Start with 14 days of everything. Then choose what fits — cancel anytime.</p>
          </Reveal>
          <Reveal delay={100} className="grid md:grid-cols-4 gap-4">
            {[
              { group: 'learner', name: 'Learner', line: 'AI classroom, lesson plans, quizzes & assignments', icon: '📘' },
              { group: 'achiever', name: 'Achiever', line: 'Adds reading & handwriting coach, language lessons, parent reports', icon: '🚀', popular: true },
              { group: 'champion', name: 'Champion', line: soonKeys.has('avatar_live_tutor')
                ? 'Adds live classes and the most voice-teacher time — face-to-face avatar coming soon'
                : 'Adds the face-to-face avatar and live classes, with the most voice-teacher time', icon: '🏆' },
            ].map(t => {
              const p = monthly(t.group);
              return (
                <div key={t.group} className={`rounded-2xl p-5 border ${t.popular ? 'border-2 border-indigo-500 shadow-lg' : 'border-slate-200'} relative`}>
                  {t.popular && <span className="absolute -top-3 left-5 text-[10px] font-bold bg-indigo-600 text-white px-2 py-0.5 rounded-full">Most popular</span>}
                  <div className="text-2xl mb-2" aria-hidden>{t.icon}</div>
                  <p className="font-bold text-slate-900">{p?.name || t.name}</p>
                  <p className="text-2xl font-extrabold text-slate-900 mt-1 min-h-[32px]">{p ? <>{inr(p.price)}<span className="text-sm font-medium text-slate-500">/mo</span></> : ''}</p>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{t.line}</p>
                </div>
              );
            })}
            <div className="rounded-2xl p-5 bg-slate-900 text-white">
              <div className="text-2xl mb-2" aria-hidden>🏫</div>
              <p className="font-bold">School</p>
              <p className="text-2xl font-extrabold mt-1 min-h-[32px]">{school ? <>{inr(school.price)}<span className="text-sm font-medium text-slate-400">/student/{school.billingCycle === 'yearly' ? 'yr' : 'mo'}</span></> : ''}</p>
              <p className="text-xs text-slate-300 mt-2 leading-relaxed">Everything above for every student, plus teaching tools and a school console</p>
            </div>
          </Reveal>
          <div className="text-center mt-8">
            <Link to="/pricing" className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-md text-sm">
              Compare all plans →
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section id="faq" className="py-20 px-5 lg:px-8 bg-slate-50 scroll-mt-16">
        <div className="max-w-3xl mx-auto">
          <Reveal><h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-center mb-10">Frequently asked questions</h2></Reveal>
          <Reveal delay={80} className="space-y-3">
            {FAQ.map((item, i) => {
              const open = openFaq === i;
              return (
                <div key={item.q} className="rounded-2xl bg-white border border-slate-200">
                  <button onClick={() => setOpenFaq(open ? null : i)} aria-expanded={open}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-slate-900">
                    {item.q}
                    <span className={`text-indigo-600 text-xl leading-none transition-transform ${open ? 'rotate-45' : ''}`} aria-hidden>+</span>
                  </button>
                  {open && <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed animate-fade-in motion-reduce:animate-none">{item.a}</p>}
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="py-20 px-5 lg:px-8 bg-indigo-600">
        <Reveal className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4 tracking-tight">
            Give your child a teacher who’s always ready
          </h2>
          <p className="text-indigo-200 text-base mb-8">
            14 days of everything, free. No credit card required. Set up in minutes.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/auth/register" className="inline-flex items-center justify-center px-7 py-3.5 bg-white text-indigo-700 font-bold rounded-xl hover:bg-slate-50 transition-colors shadow-md text-sm">
              Start free trial →
            </Link>
            <Link to="/pricing" className="inline-flex items-center justify-center px-7 py-3.5 bg-indigo-700 hover:bg-indigo-800 text-white font-semibold rounded-xl transition-colors border border-indigo-500 text-sm">
              View pricing plans
            </Link>
          </div>
        </Reveal>
      </section>

      </main>

      {/* ── FOOTER ──────────────────────────────────────────── */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-5 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="col-span-2">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-sm">🎓</div>
                <span className="text-white font-bold text-sm tracking-tight">Aahana SmartTutor</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed max-w-[240px]">
                A personal AI teacher and live classes for Class 1–10 students, their parents, and their schools.
              </p>
            </div>
            {[
              { title: 'Product', items: [
                { label: 'AI Teacher', href: '#ai-teacher' },
                { label: 'Features', href: '#features' },
                { label: 'Live Classes', href: '#features' },
                { label: 'Roadmap', href: '#student-roadmap' },
              ] },
              { title: 'Plans', items: [
                { label: 'Pricing', to: '/pricing' },
                { label: 'For Schools', href: '#schools' },
                { label: 'Book a demo', demo: true },
                { label: 'FAQ', href: '#faq' },
              ] },
              { title: 'Account', items: [
                { label: 'Sign in', to: '/auth/login' },
                { label: 'Start free trial', to: '/auth/register' },
              ] },
            ].map(col => (
              <div key={col.title}>
                <h4 className="text-white text-xs font-bold uppercase tracking-wider mb-4">{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.items.map((item: { label: string; href?: string; to?: string; demo?: boolean }) => (
                    <li key={item.label}>
                      {item.href
                        ? <a href={item.href} className="text-sm hover:text-white transition-colors">{item.label}</a>
                        : item.to
                          ? <Link to={item.to} className="text-sm hover:text-white transition-colors">{item.label}</Link>
                          : <button onClick={() => { setDemoIsSchool(false); setShowDemoModal(true); }} className="text-sm hover:text-white transition-colors">{item.label}</button>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} Aahana Technologies. All rights reserved.</p>
            <p className="text-xs text-slate-600 tracking-[0.2em] uppercase">Limitless · Simple · Mastery</p>
          </div>
        </div>
      </footer>

      <BookDemoModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} isSchool={demoIsSchool} />
    </div>
  );
};
